import {
    Dialog,
    DialogContent,
} from '@/components/ui/dialog'
import { Page, Text, View, Document, StyleSheet, PDFViewer } from '@react-pdf/renderer'
import { currencyFormat } from '@/utils/stringFormatter'
import {
    ClassificadoSummaryExport,
    TotalMesExport,
} from '../useSummaryByClassificadoSpreadsheets'

interface ViewPDFProps {
    isOpen: boolean
    onClose: () => void
    summary: ClassificadoSummaryExport[]
    totalsByMonth: TotalMesExport[]
    grandTotal: number
    selectedPeriod: string
}

const styles = StyleSheet.create({
    page: {
        padding: 16,
        fontSize: 9,
    },
    header: {
        width: '100%',
        backgroundColor: '#ececec',
        padding: 6,
        marginBottom: 10,
    },
    title: {
        fontSize: 14,
        fontWeight: 'bold',
    },
    subtitle: {
        fontSize: 9,
        marginTop: 2,
        color: '#2b2b2b',
    },
    section: {
        marginBottom: 10,
        border: '0.5px solid #dadada',
    },
    sectionHeader: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        backgroundColor: '#f3f3f3',
        padding: 6,
        borderBottom: '0.5px solid #dadada',
    },
    sectionTitle: {
        fontSize: 10,
        fontWeight: 'bold',
    },
    monthRow: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderBottom: '0.5px solid #ececec',
    },
    periodHeader: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        backgroundColor: '#fafafa',
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderBottom: '0.5px solid #ececec',
    },
    empresaRow: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingHorizontal: 12,
        paddingVertical: 3,
        borderBottom: '0.5px solid #f5f5f5',
    },
    text: {
        fontSize: 8,
        color: '#2b2b2b',
    },
    textBold: {
        fontSize: 9,
        fontWeight: 'bold',
    },
    footer: {
        marginTop: 8,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        borderTop: '1px solid #dadada',
        paddingTop: 6,
    },
    emptyText: {
        fontSize: 9,
        color: '#666666',
        marginTop: 12,
    },
})

const PDFDocument = ({
    summary,
    totalsByMonth,
    grandTotal,
    selectedPeriod,
}: Omit<ViewPDFProps, 'isOpen' | 'onClose'>) => (
    <Document>
        <Page size="A4" style={styles.page}>
            <View style={styles.header}>
                <Text style={styles.title}>Resumo por Classificado</Text>
                <Text style={styles.subtitle}>
                    Período: {selectedPeriod === 'all' ? 'Todos os períodos' : selectedPeriod}
                </Text>
            </View>

            {summary.length === 0 ? (
                <Text style={styles.emptyText}>Nenhum dado disponível para o resumo.</Text>
            ) : (
                <>
                    <View style={styles.section}>
                        <View style={styles.sectionHeader}>
                            <Text style={styles.sectionTitle}>Total por Mês</Text>
                            <Text style={styles.sectionTitle}>{currencyFormat(grandTotal)}</Text>
                        </View>
                        {totalsByMonth.map((periodo) => (
                            <View key={periodo.mesAno} style={styles.monthRow} wrap={false}>
                                <Text style={styles.text}>{periodo.mesAno}</Text>
                                <Text style={styles.text}>{currencyFormat(periodo.total)}</Text>
                            </View>
                        ))}
                    </View>

                    {summary.map((group) => (
                        <View key={group.classificado} style={styles.section}>
                            <View style={styles.sectionHeader} wrap={false}>
                                <Text style={styles.sectionTitle}>{group.classificado}</Text>
                                <Text style={styles.sectionTitle}>{currencyFormat(group.total)}</Text>
                            </View>

                            {group.periodos.map((periodo) => (
                                <View key={`${group.classificado}-${periodo.mesAno}`}>
                                    <View style={styles.periodHeader} wrap={false}>
                                        <Text style={styles.textBold}>{periodo.mesAno}</Text>
                                        <Text style={styles.textBold}>{currencyFormat(periodo.total)}</Text>
                                    </View>
                                    {periodo.empresas.map((empresa) => (
                                        <View
                                            key={`${group.classificado}-${periodo.mesAno}-${empresa.empresa}`}
                                            style={styles.empresaRow}
                                            wrap={false}
                                        >
                                            <Text style={styles.text}>{empresa.empresa}</Text>
                                            <Text style={styles.text}>{currencyFormat(empresa.total)}</Text>
                                        </View>
                                    ))}
                                </View>
                            ))}
                        </View>
                    ))}

                    <View style={styles.footer} wrap={false}>
                        <Text style={styles.textBold}>Total Geral</Text>
                        <Text style={styles.textBold}>{currencyFormat(grandTotal)}</Text>
                    </View>
                </>
            )}
        </Page>
    </Document>
)

const ViewPDF = ({
    isOpen,
    onClose,
    summary,
    totalsByMonth,
    grandTotal,
    selectedPeriod,
}: ViewPDFProps) => {
    if (!isOpen) {
        return null
    }

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-w-[1000px] h-[85vh] overflow-hidden flex flex-col">
                <PDFViewer className="w-full h-full min-h-[70vh]" showToolbar>
                    <PDFDocument
                        summary={summary}
                        totalsByMonth={totalsByMonth}
                        grandTotal={grandTotal}
                        selectedPeriod={selectedPeriod}
                    />
                </PDFViewer>
            </DialogContent>
        </Dialog>
    )
}

export default ViewPDF
